import { reactive, ref, readonly, onBeforeMount, watchEffect, watchPostEffect } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { 
  getHeziAccountInfo, 
  updateHeziServiceConfig, 
  IHeziServiceConfigKeys,
  IHeziAccountInfo,
} from '@remote/api/adapter/api-http/hezi-account'
import {
  getSmbStatus,
  turnOnSmbService,
  turnOffSmbService,
  enableAutoRunSmb,
  disableAutoRunSmb,
  getSmbAccessibilityConfig,
  setSmbAccessibilityConfig,
  setSambaAccount,
} from '@remote/api/adapter/gx-mqtt'

import {
  getThumbStatus,
  getBackerStatus,
  turnOnBackerService,
  turnOffBackerService,
  enableAutoRunThumb,
  disableAutoRunThumb,
  turnOnThumbService,
  turnOffThumbService,
  getTransportSettings,
  setTransportSettings,
} from '@remote/api/adapter/shell-mqtt'

import { logInfo, logError } from '@remote/utils/logger'
import { getErrorMessage } from '@remote/utils/base'
import { checkDeviceState } from '@remote/store/utils'
import { store } from '@remote/store'

import type { TTransportConfig } from '@remote/api/adapter/shell-mqtt'

function logs (..._args: unknown[]) {
  console.log('[pinia/settings]', ..._args)
}
export type IServiceStatus = '' | 'enable' | 'disable'
interface IStoreState {
  isAutoUpgrade: boolean
  isAutoRunSmb: boolean
  isAutoRunThumb: boolean
  isAutoRunBacker: boolean
  isSmbAllowAnonymous: boolean
  isAutoDownload: boolean
}

const serviceConfigKeyMap = {
  isAutoUpgrade: 'is_service_auto',
  isAutoRunBacker: 'is_auto_run_backer',
  isAutoRunThumb: 'is_auto_run_thumb',
  isAutoRunSmb: 'is_auto_run_smb',
  isSmbAllowAnonymous: 'is_smb_allow_anonymous',
  isAutoDownload: 'is_auto_download',
}

export const useHeziSettingsStore = defineStore('hz_user_settings', () => {
  const inited = ref(false)
  const lockRef: { [key: string]: boolean } = reactive({ upgrade: false })
  const httpSettings: IStoreState = reactive({
    isAutoUpgrade: false,
    isAutoRunBacker: false,
    isAutoRunThumb: false,
    isAutoRunSmb: true,
    isSmbAllowAnonymous: true,
    isAutoDownload: true,
  })

  const smbAccount = reactive({ username: '', password: '' })

  const deviceSettings: IStoreState & {
    smbStatus: IServiceStatus
    thumbStatus: IServiceStatus
    hasSmbAccount: boolean
    smbAccount: { username: string; password: string }
  } = reactive({
    isAutoRunSmb: true,
    smbStatus: 'enable',
    hasSmbAccount: false,
    smbAccount: { username: '', password: '' },
    isAutoRunThumb: false,
    thumbStatus: 'enable',
    isAutoUpgrade: false,
    isAutoRunBacker: false,
    isSmbAllowAnonymous: true,
    isAutoDownload: true,
  })

  const heziVip = reactive<Pick<IHeziAccountInfo['vip'], 'isvip'>>({
    isvip: 0,
  })

  function getHttpSettings () {
    return getHeziAccountInfo().then(response => {
      const { code, data, msg } = response
      if (code !== 200) {
        throw new Error(msg)
      }
      const {
        is_service_auto,
        is_auto_run_backer,
        is_auto_run_thumb,
        is_auto_run_smb,
        is_smb_allow_anonymous,
        is_auto_download,
        vip,
      } = data

      const settings: IStoreState = {
        isAutoUpgrade: !!is_service_auto,
        isAutoRunBacker: !!is_auto_run_backer,
        isAutoRunThumb: !!is_auto_run_thumb,
        isAutoRunSmb: !!is_auto_run_smb,
        isSmbAllowAnonymous: !!is_smb_allow_anonymous,
        isAutoDownload: !!is_auto_download,
      }

      heziVip.isvip = vip.isvip
      logs('当前设置:', settings, vip)

      httpSettings.isAutoUpgrade = settings.isAutoUpgrade
      httpSettings.isAutoRunBacker = settings.isAutoRunBacker
      httpSettings.isAutoRunThumb = settings.isAutoRunThumb
      httpSettings.isAutoRunSmb = settings.isAutoRunSmb
      httpSettings.isSmbAllowAnonymous = settings.isSmbAllowAnonymous
      httpSettings.isAutoDownload = settings.isAutoDownload

      inited.value = true

      return settings
    })
  }
  // 以http服务器配置为基准校验设备配置
  async function checkSettings (setting: keyof typeof serviceConfigKeyMap, startStopService?:boolean) {
    switch (setting) {
      case 'isAutoRunBacker':
        if (httpSettings.isAutoRunBacker !== deviceSettings.isAutoRunBacker || startStopService) {
          if (httpSettings.isAutoRunBacker) {
            await turnOnBackerService()
          } else {
            await turnOffBackerService()
          }
        }
        deviceSettings.isAutoRunBacker = httpSettings.isAutoRunBacker
        break
      case 'isAutoRunThumb':
        if (httpSettings.isAutoRunThumb !== deviceSettings.isAutoRunThumb || startStopService) {
          if (httpSettings.isAutoRunThumb) {
            await enableAutoRunThumb()
            if (startStopService) await turnOnThumbService()
          } else {
            await disableAutoRunThumb()
            if (startStopService) await turnOffThumbService()
          }
        }
        deviceSettings.isAutoRunThumb = httpSettings.isAutoRunThumb
        break
      case 'isAutoRunSmb':
        if (httpSettings.isAutoRunSmb !== deviceSettings.isAutoRunSmb || startStopService) {
          if (httpSettings.isAutoRunSmb) {
            await enableAutoRunSmb()
            if (startStopService) await turnOnSmbService()
          } else {
            await disableAutoRunSmb()
            if (startStopService) await turnOffSmbService
          }
        }
        deviceSettings.isAutoRunSmb = httpSettings.isAutoRunSmb
        break
      case 'isSmbAllowAnonymous':
        if (httpSettings.isSmbAllowAnonymous !== deviceSettings.isSmbAllowAnonymous || startStopService) {
          await setSmbAccessibilityConfig(httpSettings.isSmbAllowAnonymous)
        }
        deviceSettings.isSmbAllowAnonymous = httpSettings.isSmbAllowAnonymous
        break
      default:
        break
    }
  }

  function getDeviceSettings () {
    // 共享服务
    getSmbStatus()
      .then(data => {
        logs('共享服务状态:', data.result)
        if (data.ret === 0 && data.result) {
          const { enabled, status } = data.result
          const smbStatus: IServiceStatus = status === 'running' ? 'enable' : (status === 'stopped' ? 'disable' : '')
          deviceSettings.isAutoRunSmb = enabled
          deviceSettings.smbStatus = smbStatus
          checkSettings('isAutoRunSmb')
          return { isAutoRunSmb: enabled, smbStatus }
        }
      })
    // 转码服务
    getThumbStatus()
      .then(result => {
        logs('转码服务状态:', result)
        const { enabled, status } = result
        // setAutoRunThumb(enabled)
        const thumbStatus: IServiceStatus = status === 'running' ? 'enable' : (status === 'stopped' ? 'disable' : '')
        deviceSettings.isAutoRunThumb = enabled
        deviceSettings.thumbStatus = thumbStatus
        checkSettings('isAutoRunThumb')
        return { isAutoRunThumb: enabled, thumbStatus }
      })
    // 备份服务
    getBackerStatus()
      .then(result => {
        const [isAutoRunBacker] = result
        deviceSettings.isAutoRunBacker = isAutoRunBacker
        checkSettings('isAutoRunBacker')
        return { isAutoRunBacker }
      })

    getSmbAccessibilityConfig()
      .then(result => {
        const { isPublic, account } = result
        logs('用户Samba账号信息:', account)
        deviceSettings.isSmbAllowAnonymous = !!isPublic
        deviceSettings.hasSmbAccount = !!account
        if (account) {
          deviceSettings.smbAccount = account
          smbAccount.username = account.username
          smbAccount.password = account.password
        }
        checkSettings('isSmbAllowAnonymous')
        return { isSmbAllowAnonymous: !!isPublic, hasSmbAccount: !!account }
      })
  }

  async function toggleAutograde (isOpened = !httpSettings.isAutoUpgrade) {
    if (lockRef.upgrade) return Promise.reject(new Error('操作频繁，请稍后重试'))

    lockRef.upgrade = true
    const before = httpSettings.isAutoUpgrade
    const traceId = uuidv4()
    logInfo('app', {
      traceId,
      category: 'about_app',
      action: 'set_auto_upgrade_service',
      desc: (isOpened ? '启用' : '关闭') + '自动服务升级',
      params: { isAutoUpgrade: isOpened },
    })
    httpSettings.isAutoUpgrade = isOpened
    try {
      await updateHeziServiceConfig({ is_service_auto: isOpened ? 1 : 0 })
      logInfo('app', {
        traceId,
        category: 'about_app',
        action: 'set_auto_upgrade_service_succ',
        desc: (isOpened ? '启用' : '关闭') + '自动服务升级成功',
        params: { isAutoUpgrade: isOpened },
      })
    } catch (err) {
      console.error(err)
      httpSettings.isAutoUpgrade = before
      logError('app', {
        traceId,
        category: 'about_app',
        action: 'set_auto_upgrade_service_fail',
        desc: (isOpened ? '启用' : '关闭') + '自动服务升级时出现异常',
        params: { isAutoUpgrade: isOpened },
        error: err,
        errorText: getErrorMessage(err),
      })
    } finally {
      lockRef.upgrade = false
    }
  }

  type TToggleSetHeziServiceKeys = keyof typeof httpSettings
  async function toggleSetHeziService (key: TToggleSetHeziServiceKeys, value?: boolean) {
    if (lockRef[key]) return Promise.reject(new Error('操作频繁，请稍后重试'))

    lockRef[key] = true
    const isOpened = typeof value === 'undefined' ? !httpSettings[key] : value
    const before = httpSettings[key]
    httpSettings[key] = isOpened
    console.log('toggleSetHeziService', key, value, isOpened)
    try {
      await updateHeziServiceConfig({ [serviceConfigKeyMap[key] as IHeziServiceConfigKeys]: isOpened ? 1 : 0 })
      try {
        if (checkDeviceState(true)) {
          return Promise.reject('设置成功，待设备在线后生效')
        } else {
          await checkSettings(key, true)
        }
      } catch (err) {
        console.log(err)
        return Promise.reject('设置成功，待设备重启后生效')
      }
      return Promise.resolve()
    } catch (err) {
      console.error(err)
      httpSettings[key] = before
      return Promise.reject(err)
    } finally {
      lockRef[key] = false
    }
  }

  function updateSMBAccount (account: { username: string; password: string }) {
    return setSambaAccount(account).then(() => {
      smbAccount.username = account.username
      smbAccount.password = account.password
    })
  }

  function refreshSettings () {
    if (!inited.value) return
    getHttpSettings().then(getDeviceSettings)
  }

  onBeforeMount(() => {
    console.log('[pinia_settings_store][onBeforeMount]:', inited)
    if (inited.value) return
    getHttpSettings().then(getDeviceSettings)
  })

  return { 
    state: httpSettings, 
    smbAccount,
    heziVip,
    getSettings: getHttpSettings, 
    toggleAutograde, 
    toggleSetHeziService, 
    updateSMBAccount,
    refreshSettings,
  }
})

interface ITransportSettingState extends TTransportConfig {
  /** 可以设置的最大同时下载数 */
  concurrent_download_limit: number
  /** 开机自动下载 */
  is_auto_download: boolean
}

export const useHeziTransportSettingStore = defineStore('hz_transport_setting', () => {
  // const updateId = ref(0)
  const lockRef = reactive({ setting: false })
  const { state: userSettings, toggleSetHeziService } = useHeziSettingsStore()
  const settings: ITransportSettingState = reactive({
    is_auto_download: userSettings.isAutoDownload,
    concurrent_download_limit: 20,
    concurrent_downloads: 5,
    download_speed_limit: -1,
    upload_speed_limit: -1,
    downloads_directory: '',
  })
  watchEffect(() => settings.is_auto_download = userSettings.isAutoDownload)
  
  const inited = ref(false)
  function initSetting () {
    getTransportSettings().then(response => {
      const { ret, result } = response
      if (ret === 0 && result) {
        settings.concurrent_downloads = result.concurrent_downloads
        settings.download_speed_limit = result.download_speed_limit
        settings.downloads_directory = result.downloads_directory
        settings.upload_speed_limit = result.upload_speed_limit
      }
    }).finally(() => {
      inited.value = true
    })
  }

  function updateTransportSettings (config: Partial<TTransportConfig>) {
    if (lockRef.setting) return Promise.reject(new Error('操作频繁，请稍后重试'))

    lockRef.setting = true
    const prevSetting = readonly(settings)
    const { concurrent_download_limit: _, is_auto_download, ...current } = settings
    const _settings = Object.assign({}, current, config)
    Object.keys(config).forEach(key => {
      settings[key] = config[key]
    })
    return setTransportSettings(_settings).then(() => {
    }).catch(err => {
      // 设置失败，回到上一次的设置
      Object.keys(config).forEach(key => {
        settings[key] = prevSetting[key]
      })
      throw err
    }).finally(() => {
      lockRef.setting = false
    })
  }

  function toggleAutoDownload (isOpened = !settings.is_auto_download) {
    const before = settings.is_auto_download
    settings.is_auto_download = isOpened
    return toggleSetHeziService('isAutoDownload', isOpened).catch(err => {
      settings.is_auto_download = before
      throw err
    })
  }

  watchPostEffect(() => {
    const target = store.state.curDeviceRunner.params?.target
    if (target) {
      // 设备连接，开始获取设置项
      initSetting()
    }
  })

  return {
    state: settings,
    updateTransportSettings,
    toggleAutoDownload,
  }
})

